import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import { getPageMetaImage } from "utils";
import {
  type Banner,
  type BlogSimple,
  type Page as SanityPage,
  getAnnouncementBanner,
  getFeaturedBlogs,
  getPage,
} from "lib/sanity";
import { Page } from "components/page";
import { Section, sectionSpacingY } from "components/section";
import {
  GridHeading,
  LearnHero,
  SectionGrid,
  BlogCard,
  HorizontalFeatureCard,
  VerticalFeatureCard,
} from "components/learn";
import { DirectionalButton } from "ui/button";
import { Text } from "@chakra-ui/react";
import { getBrandColorHex } from "theme/colors";
import cdpCardImage from "../../public/assets/learn/cdp-card.png";
import compareCdpImage from "../../public/assets/learn/compare-cdp-card.png";
import cdpIndustriesImage from "../../public/assets/learn/cdp-industries-card.png";
import sqlDictionaryImage from "../../public/assets/learn/sql-dictionary-card.png";

type PageProps = Readonly<{
  page: SanityPage;
  announcementBanner?: Banner;
  martechBlogs: BlogSimple[];
  marketerBlogs: BlogSimple[];
  dataBlogs: BlogSimple[];
  advertiserBlogs: BlogSimple[];
}>;

const Learn: NextPage<PageProps> = ({
  page,
  announcementBanner,
  martechBlogs,
  marketerBlogs,
  dataBlogs,
  advertiserBlogs,
}) => {
  return (
    <Page
      hasSmoothScroll
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.950"
      theme="dark"
      footerOptions={{ variant: "blue" }}
    >
      <LearnHero title={page.title} />

      <Section id="marketing-technology" my={0} pt={sectionSpacingY}>
        <GridHeading
          title="Marketing technology"
          action={
            <DirectionalButton href="/blog/category/marketing-technology/page/1" pl={{ base: 0, md: undefined }}>
              See all marketing technology posts
            </DirectionalButton>
          }
        />

        <HorizontalFeatureCard
          bgColor="new.gray.950"
          href="/solutions/composable-cdp"
          buttonText="Learn more"
          title="Everything you need to know about the Composable CDP"
          description="Discover what makes a Customer Data Platform composable, hear from industry leaders, and find the best partners for your project."
          image={cdpCardImage}
          imageRatio={423 / 332}
        />

        <SectionGrid mt="big">
          <VerticalFeatureCard
            bgColor="new.green.950"
            spotlightColor={getBrandColorHex("green", 900)}
            title="See how different industries benefit from CDPs"
            href="/cdp-industries"
            buttonText="Explore CDP Industries Hub"
            image={cdpIndustriesImage}
            imageRatio={424 / 220}
          />

          {martechBlogs.slice(0, 1).map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}

          <VerticalFeatureCard
            bgColor="new.violet.950"
            spotlightColor={getBrandColorHex("violet", 900)}
            title="Compare any Customer Data Platform"
            href="/compare-cdps"
            buttonText="Explore comparison tables"
            image={compareCdpImage}
            imageRatio={424 / 220}
          />

          {martechBlogs.slice(1).map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </SectionGrid>
      </Section>

      <Section id="marketers" my={0} py={sectionSpacingY}>
        <GridHeading
          title="For marketers"
          action={
            <DirectionalButton href="/blog/category/marketers/page/1" pl={{ base: 0, md: undefined }}>
              See all marketing posts
            </DirectionalButton>
          }
        />
        <SectionGrid>
          {marketerBlogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </SectionGrid>
      </Section>

      <Section id="data-and-analytics" bg="brand.backgroundSecondary.light">
        <GridHeading
          title="For data and analytics engineers"
          action={
            <DirectionalButton href="/blog/category/data-and-analytics/page/1" pl={{ base: 0, md: undefined }}>
              See all data posts
            </DirectionalButton>
          }
        />

        <HorizontalFeatureCard
          mt="biggest"
          bgColor="new.blue.900"
          href="/sql-dictionary"
          buttonText="Learn more"
          title="The Complete SQL Dictionary"
          description={
            <Text as="span" color="brand.text.dark">
              Get clear syntax, use cases, and examples of every useful SQL function you need to find insights and
              automate your data.
            </Text>
          }
          image={sqlDictionaryImage}
          imageRatio={600 / 360}
        />

        <SectionGrid mt="big">
          {dataBlogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </SectionGrid>
      </Section>

      <Section id="advertisers" my={0} py={sectionSpacingY}>
        <GridHeading
          title="For advertisers"
          action={
            <DirectionalButton href="/blog/category/advertisers/page/1" pl={{ base: 0, md: undefined }}>
              See all advertising posts
            </DirectionalButton>
          }
        />

        <SectionGrid>
          {advertiserBlogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </SectionGrid>
      </Section>
    </Page>
  );
};

const martechBlogSlugs = [
  "composable-cdp",
  "ai-decisioning",
  "ai-decisioning-use-cases",
  "martech-stack",
  "what-is-a-customer-data-platform-cdp",
  "reverse-etl",
  "event-tracking",
];

const marketerBlogSlugs = ["marketing-campaigns", "lifecycle-marketing", "marketing-analytics"];

const dataBlogSlugs = ["customer-data-warehouse", "data-collection", "data-modeling"];

const advertiserBlogSlugs = ["adtech", "conversion-apis", "data-clean-room"];

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, martechBlogs, marketerBlogs, dataBlogs, advertiserBlogs] = await Promise.all([
    getPage("/learn"),
    getAnnouncementBanner(),
    getFeaturedBlogs(martechBlogSlugs),
    getFeaturedBlogs(marketerBlogSlugs),
    getFeaturedBlogs(dataBlogSlugs),
    getFeaturedBlogs(advertiserBlogSlugs),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      martechBlogs,
      marketerBlogs,
      dataBlogs,
      advertiserBlogs,
    },
    revalidate: 60,
  };
};

export default Learn;
